import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { platformService, type PlatformPresence } from '../services/platform';
import { ApiError } from '../services/api';
import { normalizeDeskWorkspace } from '@detect/shared';

/**
 * Signed in but no tenant memberships — the SELF-SERVE SIGNUP entry
 * (desk-integration.md §9): the customer creates their organization,
 * answering the "existing Desk/Dispatch customer?" question themselves.
 * Never an error page.
 */

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

/**
 * The tenant-slug contract from `@detect/shared` (`slugSchema`: a 2–63
 * character DNS label). A directory id that doesn't satisfy it is not a
 * candidate — seeding a value that signup will only reject later turns
 * another product's bad row into this form's problem.
 */
const SLUG_SHAPE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
const SLUG_MIN = 2;
const SLUG_MAX = 63;

function isSlugShaped(id: string): boolean {
  return id.length >= SLUG_MIN && id.length <= SLUG_MAX && SLUG_SHAPE.test(id);
}

/**
 * The organization's name as the family already knows it. The directory
 * carries the name the customer typed into whichever product they signed
 * up for first; the domain label ("teamtech" for teamtech.guru) is only a
 * last resort — it is a DNS artifact, not a company name.
 */
function presenceName(presence: PlatformPresence): string {
  const named = presence.products.find((p) => p.displayName);
  if (named?.displayName) return named.displayName;
  const label = presence.domain?.split('.')[0] ?? '';
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : '';
}

/**
 * The org id its sibling products already show customers, when they agree
 * on one — "ttg", not "team-tech-guru". One id across the family is the
 * property worth having.
 *
 * Reads `productSlug` ONLY. `productTenantId` is the writer's storage key
 * and is not interchangeable with it: Desk stores `tenant-ttg` and shows
 * `ttg`, so seeding from the tenant id offered a customer `tenant-ttg` as
 * their permanent organization id. The prefix can't be stripped either —
 * it isn't systematic across tenants.
 *
 * Disagreement, or no slug at all, falls back to the org name. Still only
 * a DEFAULT: signup re-validates against `slugSchema` and the uniqueness
 * check, so a stale or hostile directory row seeds a suggestion, never
 * an id.
 */
function presenceSlug(
  presence: PlatformPresence,
  name: string
): { slug: string; fromDirectory: boolean } {
  const ids = [
    ...new Set(
      presence.products
        .map((p) => p.productSlug?.trim().toLowerCase() ?? '')
        .filter(isSlugShaped)
    ),
  ];
  return ids.length === 1
    ? { slug: ids[0]!, fromDirectory: true }
    : { slug: slugify(name), fromDirectory: false };
}

export function NoTenant() {
  const { user, signOut, refreshMe } = useAuth();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  /**
   * The identifier came from the family, so it is not a derived value any
   * more — editing the organization name must not silently rewrite it
   * (and "changing the name back" cannot restore it, since the derived
   * form was never what the directory said). Pinned means: never
   * auto-change, ever again. `slugUnlocked` is the deliberate escape
   * hatch, for the case where the id is taken or genuinely wrong.
   */
  const [slugPinned, setSlugPinned] = useState(false);
  const [slugUnlocked, setSlugUnlocked] = useState(false);
  const [contactEmail, setContactEmail] = useState(user?.email ?? '');
  const [isMeshtek, setIsMeshtek] = useState(false);
  const [deskTenantId, setDeskTenantId] = useState('');
  const [serviceProviderId, setServiceProviderId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [presence, setPresence] = useState<PlatformPresence | null>(null);

  /**
   * Typing races the directory: the lookup can resolve at any time, and
   * the effect's closure sees the state as it was on FIRST render, so
   * `if (!name)` would still be true against a field the customer has
   * since filled in. These refs are read live, so a slow answer can never
   * overwrite work already done. Identity and linkage track separately —
   * entering a Desk workspace must not suppress the name/slug seed.
   */
  const identityEditedRef = useRef(false);
  const linkageEditedRef = useRef(false);

  const timezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

  /**
   * Ask the Meshtek Platform Directory what this organization already
   * uses (auth#5) and pre-fill the linkage answers — replacing the
   * per-product identifiers a customer can rarely produce from memory.
   * Advisory only: a miss (or an unreachable directory) simply leaves
   * the manual questions as they were, and the fields stay editable.
   */
  useEffect(() => {
    let cancelled = false;
    void platformService
      .platformPresence()
      .then((result) => {
        if (cancelled) return;
        setPresence(result);
        const desk = result.products.find((p) => p.product === 'desk');
        const dispatch = result.products.find((p) => p.product === 'dispatch');
        if ((desk || dispatch) && !linkageEditedRef.current) {
          setIsMeshtek(true);
          // The slug the customer recognizes, never the directory's storage key (productTenantId).
          if (desk?.productSlug) setDeskTenantId(desk.productSlug);
          if (dispatch?.productTenantId) setServiceProviderId(dispatch.productTenantId);
        }
        // Seed the identity fields from what the family already knows —
        // the sibling product's display name and org id — but only while
        // the customer hasn't typed their own.
        if (!identityEditedRef.current) {
          const seeded = presenceName(result);
          if (seeded) {
            const { slug: seededSlug, fromDirectory } = presenceSlug(
              result,
              seeded
            );
            setName(seeded);
            setSlug(seededSlug);
            if (fromDirectory) setSlugPinned(true);
          }
        }
      })
      .catch(() => {
        // Never surfaced: pre-fill is a convenience, not a step.
      });
    return () => {
      cancelled = true;
    };
    // Runs once on mount — later edits must never be overwritten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const foundProducts = presence?.products ?? [];
  /**
   * Only Desk and Dispatch have linkage fields on this form, so a match
   * on any other product recognizes the organization without filling
   * anything in — the banner must not claim otherwise.
   */
  const linkagePrefilled = foundProducts.some(
    (p) => p.product === 'desk' || p.product === 'dispatch'
  );
  /**
   * Don't ask people what we already know: an organization the directory
   * returned family products for is not asked whether it is an existing
   * Meshtek customer.
   *
   * Gated on the products, NOT on `presence.found` — an entry can exist
   * while this list is empty (Detect-only org, or every sibling entry
   * suspended), and there the directory has told us nothing about Desk or
   * Dispatch, so the customer is still the only source and the question
   * is honest.
   */
  const recognized = foundProducts.length > 0;
  const linkProducts = recognized || isMeshtek;
  const PRODUCT_LABELS: Record<string, string> = {
    desk: 'Meshtek Desk',
    dispatch: 'Meshtek Dispatch',
    gendesk: 'Meshtek GenDesk',
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    // The form is `noValidate`, so `required`/`pattern` on the inputs are
    // advisory — check the slug here or an unusable value (a name that
    // slugifies to nothing, a hand-edited id) reaches the API and comes
    // back as a generic failure pointing at no field in particular.
    // Normalize ONCE and send exactly what was validated — checking one
    // string and posting another is how "it passed the client check and
    // failed server-side" happens.
    const normalizedSlug = slug.trim().toLowerCase();
    if (!isSlugShaped(normalizedSlug)) {
      setError(
        'The URL identifier must be 2–63 characters: lowercase letters, numbers and hyphens, not starting or ending with a hyphen.'
      );
      return;
    }
    setSubmitting(true);
    try {
      await platformService.signup({
        name: name.trim(),
        slug: normalizedSlug,
        contactEmail: contactEmail.trim(),
        timezone,
        ...(linkProducts && deskTenantId
          ? { desk: { deskTenantId: normalizeDeskWorkspace(deskTenantId) } }
          : {}),
        ...(linkProducts && serviceProviderId
          ? { dispatch: { serviceProviderId } }
          : {}),
      });
      await refreshMe();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Sign-up failed. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={onSubmit} noValidate>
        <h1 className="login-title">Create your organization</h1>
        <p className="login-subtitle">
          {user?.email} isn’t part of an organization yet. Set one up — it
        takes a minute.
        </p>
        {error && (
          <div className="error-state" role="alert">
            {error}
          </div>
        )}

        {foundProducts.length > 0 && (
          <div className="info-state" role="status">
            <strong>We recognize {presence?.domain}.</strong> Your
            organization already uses{' '}
            {foundProducts
              .map((p) => PRODUCT_LABELS[p.product] ?? p.product)
              .join(' and ')}
            {linkagePrefilled
              ? ', so we’ve filled those in below. Nothing connects until you enable it in Settings.'
              : ', so we’ve started you off with its name and id. Nothing connects until you set it up in Settings.'}
          </div>
        )}

        {!recognized && (
          <fieldset className="form-field">
            <legend>Are you an existing Meshtek customer?</legend>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={isMeshtek}
                onChange={(e) => {
                  linkageEditedRef.current = true;
                  setIsMeshtek(e.target.checked);
                }}
              />
              We already use the Meshtek platform (Desk, Dispatch, …)
            </label>
          </fieldset>
        )}

        {linkProducts && (
          <fieldset className="form-field">
            <legend>Connect your Meshtek products</legend>
            <p className="field-hint">
              {linkagePrefilled
                ? 'Pre-filled from your Meshtek account — edit if anything looks wrong. Nothing connects until you enable it in Settings.'
                : 'Fill in whichever you use — this pre-wires the integrations (nothing connects until you enable them in Settings).'}
            </p>
            <div className="form-field">
              <label htmlFor="signup-desk-tenant">Desk workspace</label>
              <input
                id="signup-desk-tenant"
                value={deskTenantId}
                onChange={(e) => {
                  linkageEditedRef.current = true;
                  setDeskTenantId(e.target.value);
                }}
                placeholder="ttg · ttg.desk.meshtek.io · support.yourdomain.com"
              />
              <p className="field-hint">
                Any form works: your workspace name, its full
                desk.meshtek.io address, or your custom support domain.
              </p>
            </div>
            <div className="form-field">
              <label htmlFor="signup-sp-id">
                Dispatch service provider id
              </label>
              <input
                id="signup-sp-id"
                value={serviceProviderId}
                onChange={(e) => {
                  linkageEditedRef.current = true;
                  setServiceProviderId(e.target.value);
                }}
                placeholder="optional"
              />
            </div>
          </fieldset>
        )}

        <div className="form-field">
          <label htmlFor="signup-name">Organization name</label>
          <input
            id="signup-name"
            value={name}
            onChange={(e) => {
              identityEditedRef.current = true;
              setName(e.target.value);
              // A pinned id is the family's, not a shadow of this field.
              if (!slugTouched && !slugPinned) setSlug(slugify(e.target.value));
            }}
            required
          />
        </div>
        <div className="form-field">
          <label htmlFor="signup-slug">URL identifier</label>
          <input
            id="signup-slug"
            value={slug}
            onChange={(e) => {
              identityEditedRef.current = true;
              setSlugTouched(true);
              setSlug(e.target.value);
            }}
            readOnly={slugPinned && !slugUnlocked}
            pattern="[a-z0-9]([a-z0-9\-]*[a-z0-9])?"
            required
          />
          {slugPinned ? (
            <p className="field-hint">
              This is the id your organization already uses across Meshtek —
              we keep it the same here so one organization means one id.
              {!slugUnlocked && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => {
                      // The customer has taken over the identity fields:
                      // hold every guard that means that, not just the
                      // one this button strictly needs.
                      identityEditedRef.current = true;
                      setSlugUnlocked(true);
                      setSlugTouched(true);
                    }}
                  >
                    Use a different id
                  </button>
                </>
              )}
            </p>
          ) : (
            <p className="field-hint">
              Lowercase letters, numbers, hyphens. This becomes your permanent
              organization id.
            </p>
          )}
        </div>
        <div className="form-field">
          <label htmlFor="signup-email">Contact email</label>
          <input
            id="signup-email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            required
          />
        </div>

        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Creating…' : 'Create organization'}
        </button>

        <div className="login-divider" role="separator">
          or
        </div>
        {user?.globalRole === 'superadmin' && (
          <Link className="btn btn-secondary btn-block" to="/platform">
            Platform admin
          </Link>
        )}
        <button
          type="button"
          className="btn btn-secondary btn-block"
          onClick={() => void signOut()}
        >
          Sign out
        </button>
      </form>
    </div>
  );
}
